import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, FileText, Upload, ArrowLeft, Search, Filter,
  File, Clock, Trash2, Eye, FileCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Documents = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [documents, setDocuments] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadWorkspace, setUploadWorkspace] = useState("");
  const [uploadMatter, setUploadMatter] = useState("");
  const [uploadDocType, setUploadDocType] = useState("general");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, wsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/documents`),
        axios.get(`${BACKEND_URL}/api/workspaces`)
      ]);
      setDocuments(docsRes.data);
      setWorkspaces(wsRes.data);
      
      if (wsRes.data.length > 0) {
        setUploadWorkspace(wsRes.data[0].workspace_id);
        // Fetch matters for first workspace
        const mattersRes = await axios.get(`${BACKEND_URL}/api/workspaces/${wsRes.data[0].workspace_id}/matters`);
        setMatters(mattersRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleWorkspaceChange = async (wsId) => {
    setUploadWorkspace(wsId);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/workspaces/${wsId}/matters`);
      setMatters(res.data);
      setUploadMatter("");
    } catch (error) {
      console.error("Error fetching matters:", error);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadWorkspace) {
      toast.error("Please select a file and workspace");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("workspace_id", uploadWorkspace);
    formData.append("matter_id", uploadMatter || "");
    formData.append("doc_type", uploadDocType);

    try {
      const res = await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDocuments(prev => [res.data, ...prev]);
      toast.success("Document uploaded successfully");
      setUploadDialogOpen(false);
      setUploadFile(null);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || doc.doc_type === filterType;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10]" data-testid="documents-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
              data-testid="back-btn"
              className="text-[#94A3B8] hover:text-[#E2E8F0]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <Scale className="w-6 h-6 text-[#C5A059]" />
              <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">Documents</span>
            </div>
          </div>
          
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="upload-document-btn"
                className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#15171C] border-white/10">
              <DialogHeader>
                <DialogTitle className="font-playfair text-[#E2E8F0]">Upload Document</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">File</Label>
                  <div className="mt-2 border-2 border-dashed border-white/10 rounded-sm p-6 text-center hover:border-[#C5A059]/50 transition-colors">
                    <input
                      type="file"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="hidden"
                      id="file-upload"
                      data-testid="file-input"
                      accept=".pdf,.docx,.txt,.doc"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {uploadFile ? (
                        <div className="flex items-center justify-center gap-2 text-[#C5A059]">
                          <FileCheck className="w-5 h-5" />
                          <span className="font-manrope text-sm">{uploadFile.name}</span>
                        </div>
                      ) : (
                        <div>
                          <Upload className="w-8 h-8 text-[#64748B] mx-auto mb-2" />
                          <p className="text-[#94A3B8] font-manrope text-sm">Click to select file</p>
                          <p className="text-[#64748B] font-mono text-xs mt-1">PDF, DOCX, TXT</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Workspace</Label>
                  <Select value={uploadWorkspace} onValueChange={handleWorkspaceChange}>
                    <SelectTrigger className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]" data-testid="workspace-select">
                      <SelectValue placeholder="Select workspace" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#15171C] border-white/10">
                      {workspaces.map(ws => (
                        <SelectItem key={ws.workspace_id} value={ws.workspace_id} className="text-[#E2E8F0]">
                          {ws.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Matter (Optional)</Label>
                  <Select value={uploadMatter} onValueChange={setUploadMatter}>
                    <SelectTrigger className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]" data-testid="matter-select">
                      <SelectValue placeholder="Select matter" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#15171C] border-white/10">
                      {matters.map(m => (
                        <SelectItem key={m.matter_id} value={m.matter_id} className="text-[#E2E8F0]">
                          {m.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-[#94A3B8] font-mono text-xs uppercase">Document Type</Label>
                  <Select value={uploadDocType} onValueChange={setUploadDocType}>
                    <SelectTrigger className="mt-2 bg-[#0B0C10] border-white/10 text-[#E2E8F0]" data-testid="doctype-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#15171C] border-white/10">
                      <SelectItem value="general" className="text-[#E2E8F0]">General</SelectItem>
                      <SelectItem value="contract" className="text-[#E2E8F0]">Contract</SelectItem>
                      <SelectItem value="advice" className="text-[#E2E8F0]">Advice Note</SelectItem>
                      <SelectItem value="pleading" className="text-[#E2E8F0]">Pleading</SelectItem>
                      <SelectItem value="memo" className="text-[#E2E8F0]">Memo</SelectItem>
                      <SelectItem value="template" className="text-[#E2E8F0]">Template</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                  data-testid="confirm-upload-btn"
                  className="w-full bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm uppercase text-xs tracking-wide"
                >
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Filters */}
      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <Input 
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-documents"
              className="pl-10 bg-[#15171C] border-white/10 text-[#E2E8F0] placeholder:text-[#64748B] rounded-sm"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 bg-[#15171C] border-white/10 text-[#E2E8F0]" data-testid="filter-type">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-[#15171C] border-white/10">
              <SelectItem value="all" className="text-[#E2E8F0]">All Types</SelectItem>
              <SelectItem value="contract" className="text-[#E2E8F0]">Contracts</SelectItem>
              <SelectItem value="advice" className="text-[#E2E8F0]">Advice Notes</SelectItem>
              <SelectItem value="pleading" className="text-[#E2E8F0]">Pleadings</SelectItem>
              <SelectItem value="memo" className="text-[#E2E8F0]">Memos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Documents List */}
      <ScrollArea className="h-[calc(100vh-180px)]">
        <div className="p-6">
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 text-[#64748B] mx-auto mb-4" />
              <h3 className="font-playfair text-xl text-[#E2E8F0] mb-2">No documents found</h3>
              <p className="text-[#94A3B8] font-manrope">Upload your first document to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredDocuments.map((doc) => (
                <DocumentCard 
                  key={doc.document_id} 
                  document={doc} 
                  onReview={() => navigate(`/contract-review/${doc.document_id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

const DocumentCard = ({ document, onReview }) => {
  const getIcon = (type) => {
    switch (type) {
      case "contract": return <FileText className="w-5 h-5 text-[#C5A059]" />;
      case "advice": return <FileCheck className="w-5 h-5 text-[#10B981]" />;
      default: return <File className="w-5 h-5 text-[#4A6FA5]" />;
    }
  };

  return (
    <Card 
      className="bg-[#15171C] border-white/5 rounded-sm p-4 hover:border-[#C5A059]/30 transition-all duration-300 hover:-translate-y-1"
      data-testid={`document-card-${document.document_id}`}
    >
      <div className="flex items-start gap-3">
        {getIcon(document.doc_type)}
        <div className="flex-1 min-w-0">
          <h4 className="text-[#E2E8F0] font-manrope text-sm font-medium truncate">
            {document.filename}
          </h4>
          <p className="text-[#64748B] font-mono text-xs mt-1">
            {(document.file_size / 1024).toFixed(1)} KB
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[#C5A059] font-mono text-xs uppercase bg-[#C5A059]/10 px-2 py-0.5 rounded-sm">
              {document.doc_type}
            </span>
            <span className="text-[#64748B] font-mono text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(document.created_at).toLocaleDateString()}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/5">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={onReview}
          data-testid={`review-btn-${document.document_id}`}
          className="text-[#C5A059] hover:bg-[#C5A059]/10 text-xs"
        >
          <Eye className="w-4 h-4 mr-1" /> Review
        </Button>
      </div>
    </Card>
  );
};

export default Documents;
